import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { sectionService, SectionDto, UpdateSectionRequest } from '@/features/admin/services/sectionService';
import { uploadService } from '@/features/admin/services/uploadService';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ArrowRight, Layers } from 'lucide-react';

const updateSectionSchema = z.object({
  name: z.string().min(1, 'اسم القسم مطلوب').max(200, 'الاسم طويل جداً'),
  description: z.string().optional(),
  displayOrder: z.number().min(0).default(0),
  isActive: z.boolean().default(true),
});

type UpdateSectionFormData = z.infer<typeof updateSectionSchema>;

export const EditSectionPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState<SectionDto | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpdateSectionFormData>({
    resolver: zodResolver(updateSectionSchema),
  });

  useEffect(() => {
    if (id) {
      fetchSection(id);
    }
  }, [id]);

  const fetchSection = async (sectionId: string) => {
    try {
      setLoading(true);
      const data = await sectionService.getSectionById(sectionId);
      setSection(data);
      setImageUrl(data.imageUrl || null);
      setIconUrl(data.iconUrl || null);
      reset({
        name: data.name,
        description: data.description || '',
        displayOrder: data.displayOrder,
        isActive: data.isEnabled,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تحميل القسم');
      navigate('/admin/sections');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: UpdateSectionFormData) => {
    if (!id) return;

    try {
      const request: UpdateSectionRequest = {
        id,
        name: data.name,
        description: data.description || undefined,
        imageUrl: imageUrl || undefined,
        iconUrl: iconUrl || undefined,
        displayOrder: data.displayOrder,
        isActive: data.isActive,
      };

      await sectionService.updateSection(id, request);
      toast.success('تم تحديث القسم بنجاح');
      navigate('/admin/sections');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تحديث القسم');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-accent-greenLight/20 rounded-lg">
          <Layers className="w-8 h-8 text-accent-greenDark" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-1">تعديل القسم</h1>
          <p className="text-accent-dark/70">تعديل معلومات القسم "{section?.name}"</p>
        </div>
      </div>

      <Card className="border-2 border-accent-greenLight/20">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-accent-dark">معلومات القسم</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                اسم القسم *
              </label>
              <Input
                {...register('name')}
                placeholder="مثال: الرياضة، التاريخ، العلوم..."
                error={errors.name?.message}
                className="text-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                وصف القسم
              </label>
              <textarea
                {...register('description')}
                placeholder="وصف مختصر للقسم..."
                rows={3}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                صورة القسم
              </label>
              <ImageUpload
                value={imageUrl || undefined}
                onChange={(url) => setImageUrl(url)}
                onUpload={uploadService.uploadSectionImage}
                placeholder="ارفع صورة للقسم"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                أيقونة القسم (لوجو)
              </label>
              <ImageUpload
                value={iconUrl || undefined}
                onChange={(url) => setIconUrl(url)}
                onUpload={uploadService.uploadSectionImage}
                placeholder="ارفع أيقونة للقسم"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                ترتيب العرض
              </label>
              <Input
                type="number"
                {...register('displayOrder', { valueAsNumber: true })}
                placeholder="0"
                error={errors.displayOrder?.message}
                className="text-lg"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                {...register('isActive')}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-accent-dark">
                القسم مفعل
              </label>
            </div>

            {/* Categories in this section */}
            {section?.categories && section.categories.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-accent-dark mb-2">الفئات في هذا القسم:</h4>
                <div className="flex flex-wrap gap-2">
                  {section.categories.map(cat => (
                    <span
                      key={cat.id}
                      className={`px-2 py-1 text-xs rounded-full ${
                        cat.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {cat.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/sections')}
                className="flex-1"
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-secondary hover:bg-secondary-dark text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    حفظ التغييرات
                    <ArrowRight className="w-4 h-4 mr-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
